<template>
    <div class="iu-upload-control-content">
        <div class="iu-frame-container" ref="framecontainer">
            <input
                type="file"
                ref="fileInput"
                accept="image/jpeg,image/png,image/webp"
                @change="onFileSelected"
                hidden
            />

            <div v-if="!sourceImage" class="iu-dropzone" @click="$refs.fileInput.click()">
                <p>{{ texts.filePickerPromptText }}</p>
            </div>

            <div v-else class="iu-frame-wrapper" ref="framewrapper">
                <canvas
                    class="iu-frame"
                    ref="canvas"
                    @pointerdown="onPointerDown"
                    @pointermove="onPointerMove"
                    @pointerup="onPointerUp"
                    @pointercancel="onPointerUp"
                    @wheel.prevent="onWheel"
                ></canvas>
                <div v-if="isLoading" class="iu-overlay">
                    <div class="iu-spinner"></div>
                </div>
            </div>
        </div>

        <div class="iu-info-container">
            <h3>{{ texts.infoTitle }}</h3>
            <br />
            <p>{{ texts.infoText }}</p>

            <div v-if="sourceImage" class="iu-controls" style="padding-top: 0.5em">
                <button class="suggestion-button" @click="$refs.fileInput.click()">{{ texts.filePickerChangeText }}</button>
                <button class="suggestion-button" @click="rotate(-1)">{{ texts.rotateLeftText }}</button>
                <button class="suggestion-button" @click="rotate(1)">{{ texts.rotateRightText }}</button>
                <button class="suggestion-button" @click="toggleFlip">{{ texts.flipText }}</button>
                <button class="suggestion-button" @click="setPortait(false)">{{ texts.orientationTextLandscape }}</button>
                <button class="suggestion-button" @click="setPortait(true)">{{ texts.orientationTextPortrait }}</button>
            </div>

            <p v-if="showResolutionWarning" class="iu-warning" role="alert">
                {{ texts.resolutionWarningText }}
                <button class="iu-warning-dismiss" @click="warningDismissed = true">×</button>
            </p>

            <p v-if="fileError" class="iu-error" role="alert">{{ fileError }}</p>
        </div>
    </div>
</template>

<script>
import { inject } from "vue";

export default {
    name: "ImageUploadControl",

    setup() {
        const config = inject("config");

        const designerVariant = config.product_variants.find(
            (variant) => Number(variant.id) === Number(config.product.productVariantId)
        );

        const texts = config.texts.imageUploadView;
        const maxZoomFactor = (config.designer && config.designer.photo_max_zoom_factor) || 6;
        const previewMaxDimension = (config.designer && config.designer.photo_preview_max_dimension) || 2000;
        const allowedFileTypes = ["image/jpeg", "image/png", "image/webp"];

        return {
            config,
            designerVariant,
            texts,
            maxZoomFactor,
            previewMaxDimension,
            allowedFileTypes,
        };
    },

    data() {
        return {
            // Full-resolution decoded photo (authoritative source for the final export)
            // and a possibly-downscaled copy used for the interactive live preview.
            sourceImage: null,
            previewImage: null,
            naturalWidth: 0,
            naturalHeight: 0,

            // Transform applied to the PHOTO content itself (independent of isPortrait).
            rotationSteps: 0, // 0..3, each step = 90° clockwise
            flipped: false, // horizontal mirror

            // Pan/zoom state, in "frame-real" pixel space (see frameRealW/frameRealH).
            zoom: 1,
            minZoom: 1,
            maxZoom: 1,
            panX: 0,
            panY: 0,

            // Output-frame orientation (the knitted product's format) — never rotates the photo.
            isPortrait: false,

            isLoading: false,
            fileError: null,
            warningDismissed: false,

            sizeX: 0,
            sizeY: 0,
            displayScale: 1,
            windowWidth: 0,
            windowHeight: 0,

            activePointers: new Map(),
            dragStart: null,
            pinchStart: null,
            renderScheduled: false,
        };
    },

    computed: {
        // "Real pixel" frame size (pre output scale_factor stretch), swapped for portrait —
        // mirrors Map.vue's invalidateView() sizing so the crop frame matches the output aspect ratio.
        frameRealW() {
            return this.isPortrait ? this.sizeY : this.sizeX;
        },
        frameRealH() {
            return this.isPortrait ? this.sizeX : this.sizeY;
        },
        // Actual output canvas pixel size (portrait-swapped).
        outW() {
            return this.isPortrait ? this.designerVariant.output_image.height : this.designerVariant.output_image.width;
        },
        outH() {
            return this.isPortrait ? this.designerVariant.output_image.width : this.designerVariant.output_image.height;
        },
        // Effective photo footprint after 90°-step rotation (90°/270° swap axes).
        effW() {
            return this.rotationSteps % 2 === 1 ? this.naturalHeight : this.naturalWidth;
        },
        effH() {
            return this.rotationSteps % 2 === 1 ? this.naturalWidth : this.naturalHeight;
        },
        // At minZoom (cover-fit, the best case), how many source pixels back one output pixel.
        // > 1 means the photo doesn't have enough native resolution for this output size.
        combinedScaleX() {
            return this.frameRealW ? this.minZoom * (this.outW / this.frameRealW) : 0;
        },
        combinedScaleY() {
            return this.frameRealH ? this.minZoom * (this.outH / this.frameRealH) : 0;
        },
        showResolutionWarning() {
            return (
                !!this.sourceImage &&
                !this.warningDismissed &&
                (this.combinedScaleX > 1.02 || this.combinedScaleY > 1.02)
            );
        },
    },

    methods: {
        // ---- File loading ----

        onFileSelected(event) {
            const file = event.target.files && event.target.files[0];
            // Allow re-selecting the same file path again later.
            event.target.value = "";
            if (!file) {
                return;
            }

            this.fileError = null;

            if (!this.allowedFileTypes.includes(file.type)) {
                this.fileError = this.texts.invalidFileTypeText;
                return;
            }

            this.isLoading = true;
            this.loadImage(file)
                .then((img) => this.setSourceImage(img))
                .catch((err) => {
                    console.error("Photo load failed", err);
                    this.fileError = this.texts.loadErrorText;
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        loadImage(file) {
            return new Promise((resolve, reject) => {
                const url = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => {
                    // Browsers decode EXIF orientation into naturalWidth/height automatically,
                    // so no manual EXIF handling is needed here.
                    URL.revokeObjectURL(url);
                    if (!img.naturalWidth || !img.naturalHeight) {
                        reject(new Error("Decoded image has zero dimensions"));
                        return;
                    }
                    resolve(img);
                };
                img.onerror = (err) => {
                    URL.revokeObjectURL(url);
                    reject(err);
                };
                img.src = url;
            });
        },

        // Downscale very large photos for smooth interactive pan/zoom. The full-resolution
        // sourceImage remains the only input used for the final export (getCurrentImage).
        buildPreviewImage(img) {
            const longestSide = Math.max(img.naturalWidth, img.naturalHeight);
            if (longestSide <= this.previewMaxDimension) {
                return img;
            }
            const scale = this.previewMaxDimension / longestSide;
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(img.naturalWidth * scale);
            canvas.height = Math.round(img.naturalHeight * scale);
            const ctx = canvas.getContext("2d");
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            return canvas;
        },

        setSourceImage(img) {
            this.sourceImage = img;
            this.naturalWidth = img.naturalWidth;
            this.naturalHeight = img.naturalHeight;
            this.previewImage = this.buildPreviewImage(img);

            this.rotationSteps = 0;
            this.flipped = false;
            this.warningDismissed = false;
            this.zoom = null; // recomputed to minZoom by invalidateView() below

            // Wait for the canvas element (v-else branch) to be mounted before sizing it.
            this.$nextTick(() => this.invalidateView());
        },

        // ---- Sizing (mirrors Map.vue's invalidateView) ----

        invalidateView() {
            if (!this.$refs.framecontainer || !this.sourceImage) {
                return;
            }

            const containerWidth = this.$refs.framecontainer.offsetWidth;

            this.sizeX = Math.round(this.designerVariant.output_image.width / this.designerVariant.scale_factor.width);
            this.sizeY = Math.round(this.designerVariant.output_image.height / this.designerVariant.scale_factor.height);

            const maxHeight = this.windowHeight * 0.7;
            const frameRealW = this.frameRealW;
            const frameRealH = this.frameRealH;

            this.displayScale = Math.min(containerWidth / frameRealW, maxHeight / frameRealH);

            const canvas = this.$refs.canvas;
            canvas.width = frameRealW;
            canvas.height = frameRealH;
            canvas.style.width = frameRealW + "px";
            canvas.style.height = frameRealH + "px";
            canvas.style.transform = `scale(${this.displayScale})`;

            this.$refs.framewrapper.style.width = frameRealW * this.displayScale + "px";
            this.$refs.framewrapper.style.height = frameRealH * this.displayScale + "px";

            this.recomputeZoomBounds();
            this.clampPan();
            this.renderPreview();
        },

        updateWindowSize() {
            this.windowWidth = window.innerWidth;
            this.windowHeight = window.innerHeight;
            this.invalidateView();
        },

        // ---- Zoom bounds / pan clamping ----

        // Minimum zoom = "cover fit": the photo always fully covers the frame, never leaving
        // empty space in the output. Because rotation is limited to 90° steps, the transformed
        // photo's bounding box in frame space is always axis-aligned, so this stays exact.
        recomputeZoomBounds() {
            if (!this.naturalWidth || !this.naturalHeight) {
                return;
            }
            const newMinZoom = Math.max(this.frameRealW / this.effW, this.frameRealH / this.effH);
            this.minZoom = newMinZoom;
            this.maxZoom = newMinZoom * this.maxZoomFactor;
            this.zoom = Math.min(this.maxZoom, Math.max(this.minZoom, this.zoom || newMinZoom));
        },

        clampPan() {
            const imgW = this.effW * this.zoom;
            const imgH = this.effH * this.zoom;
            const maxPanX = Math.max(0, (imgW - this.frameRealW) / 2);
            const maxPanY = Math.max(0, (imgH - this.frameRealH) / 2);
            this.panX = Math.min(maxPanX, Math.max(-maxPanX, this.panX));
            this.panY = Math.min(maxPanY, Math.max(-maxPanY, this.panY));
        },

        // ---- Photo transform vs. output-frame orientation (two independent functions) ----

        rotate(direction) {
            this.rotationSteps = (this.rotationSteps + direction + 4) % 4;
            this.resetZoomPan();
            this.invalidateView();
        },

        toggleFlip() {
            // Mirroring happens around the image's own center, before panning is applied, so
            // the current pan/zoom stays valid and doesn't need to be reset.
            this.flipped = !this.flipped;
            this.renderPreview();
        },

        setPortait(isPortrait) {
            if (this.isPortrait === isPortrait) {
                return;
            }
            this.isPortrait = isPortrait;
            this.resetZoomPan();
            this.invalidateView();
        },

        resetZoomPan() {
            this.zoom = null; // recomputed to the new minZoom in recomputeZoomBounds()
            this.panX = 0;
            this.panY = 0;
            this.warningDismissed = false;
        },

        // ---- Pointer / wheel interaction ----

        onPointerDown(e) {
            if (!this.sourceImage) {
                return;
            }
            this.$refs.canvas.setPointerCapture(e.pointerId);
            this.activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

            if (this.activePointers.size === 1) {
                this.dragStart = { x: e.clientX, y: e.clientY, panX: this.panX, panY: this.panY };
                this.pinchStart = null;
            } else if (this.activePointers.size === 2) {
                this.pinchStart = this.computePinchState();
                this.dragStart = null;
            }
        },

        onPointerMove(e) {
            if (!this.activePointers.has(e.pointerId)) {
                return;
            }
            this.activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

            if (this.activePointers.size === 1 && this.dragStart) {
                // Screen px -> frame-real px: divide by displayScale (same idea as Map.vue's CSS scale-down).
                this.panX = this.dragStart.panX + (e.clientX - this.dragStart.x) / this.displayScale;
                this.panY = this.dragStart.panY + (e.clientY - this.dragStart.y) / this.displayScale;
                this.clampPan();
                this.scheduleRender();
            } else if (this.activePointers.size === 2 && this.pinchStart) {
                const now = this.computePinchState();
                const nextZoom = this.pinchStart.zoom * (now.distance / this.pinchStart.distance);
                this.zoom = Math.min(this.maxZoom, Math.max(this.minZoom, nextZoom));
                this.clampPan();
                this.scheduleRender();
            }
        },

        onPointerUp(e) {
            this.activePointers.delete(e.pointerId);
            if (this.activePointers.size < 2) {
                this.pinchStart = null;
            }
            if (this.activePointers.size < 1) {
                this.dragStart = null;
            }
        },

        onWheel(e) {
            if (!this.sourceImage) {
                return;
            }
            const factor = Math.pow(1.0015, -e.deltaY);
            this.zoom = Math.min(this.maxZoom, Math.max(this.minZoom, this.zoom * factor));
            this.clampPan();
            this.scheduleRender();
        },

        computePinchState() {
            const pts = Array.from(this.activePointers.values());
            const dx = pts[0].x - pts[1].x;
            const dy = pts[0].y - pts[1].y;
            return {
                distance: Math.hypot(dx, dy) || 1,
                zoom: this.zoom,
            };
        },

        scheduleRender() {
            if (this.renderScheduled) {
                return;
            }
            this.renderScheduled = true;
            requestAnimationFrame(() => {
                this.renderScheduled = false;
                this.renderPreview();
            });
        },

        // ---- Rendering (one shared transform pipeline, two targets: preview canvas + export canvas) ----

        renderPreview() {
            if (!this.$refs.canvas || !this.previewImage) {
                return;
            }
            const ctx = this.$refs.canvas.getContext("2d");
            ctx.clearRect(0, 0, this.frameRealW, this.frameRealH);
            ctx.save();
            ctx.translate(this.frameRealW / 2, this.frameRealH / 2);
            ctx.translate(this.panX, this.panY);
            ctx.scale(this.zoom, this.zoom);
            ctx.rotate((Math.PI / 2) * this.rotationSteps);
            if (this.flipped) {
                ctx.scale(-1, 1);
            }
            // previewImage may be downscaled for performance; draw it stretched back to the
            // full naturalWidth/naturalHeight coordinate space so the transform math above
            // (which operates in natural-resolution units) stays consistent.
            ctx.drawImage(this.previewImage, -this.naturalWidth / 2, -this.naturalHeight / 2, this.naturalWidth, this.naturalHeight);
            ctx.restore();
        },

        // Public contract (matches Map.vue's getCurrentImage after its rename): returns a
        // base64 PNG data URL sized exactly to designerVariant.output_image (portrait-swapped),
        // or null on failure.
        getCurrentImage(onCompleted) {
            if (!this.sourceImage) {
                onCompleted(null);
                return;
            }
            try {
                const outW = this.outW;
                const outH = this.outH;
                // Anisotropic stretch from undistorted "frame-real" crop space into the final
                // output size — derived from designerVariant.scale_factor, applied as the
                // outermost transform so it stays correct regardless of rotation/orientation.
                const stretchX = outW / this.frameRealW;
                const stretchY = outH / this.frameRealH;

                const canvas = document.createElement("canvas");
                canvas.width = outW;
                canvas.height = outH;
                const ctx = canvas.getContext("2d");
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = "high";

                ctx.save();
                ctx.translate(outW / 2, outH / 2);
                ctx.scale(stretchX, stretchY);
                ctx.translate(this.panX, this.panY);
                ctx.scale(this.zoom, this.zoom);
                ctx.rotate((Math.PI / 2) * this.rotationSteps);
                if (this.flipped) {
                    ctx.scale(-1, 1);
                }
                ctx.drawImage(this.sourceImage, -this.naturalWidth / 2, -this.naturalHeight / 2);
                ctx.restore();

                onCompleted(canvas.toDataURL());
            } catch (err) {
                console.error("Photo rasterization failed", err);
                onCompleted(null);
            }
        },
    },

    mounted() {
        window.addEventListener("resize", this.updateWindowSize);
        this.updateWindowSize();

        this.resizeObserver = new ResizeObserver(() => {
            this.invalidateView();
        });
        this.resizeObserver.observe(this.$refs.framecontainer);
    },

    beforeUnmount() {
        window.removeEventListener("resize", this.updateWindowSize);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
};
</script>

<style>
.iu-upload-control-content {
    display: flex;
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1em;
    min-height: 60vh;
}

.iu-info-container {
    margin: 1em;
    flex: 1 1 0;
    min-width: 20vw;
    white-space: normal;
    overflow-wrap: break-word;
}

.iu-frame-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: 1 1 0;
    min-width: 60vw;
    max-height: 70vh;
}

.iu-dropzone {
    width: 100%;
    min-height: 40vh;
    border: 2px dashed #ccc;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    cursor: pointer;
    padding: 1em;
}

.iu-frame-wrapper {
    flex: 0 0 auto;
    position: relative;
    display: inline-block;
    overflow: hidden;
}

.iu-frame {
    transform-origin: top left;
    touch-action: none;
    user-select: none;
    cursor: grab;
    display: block;
}

.iu-frame:active {
    cursor: grabbing;
}

.iu-controls {
    display: flex;
    flex-wrap: wrap;
    gap: 1em;
}

.iu-warning {
    margin-top: 1em;
    padding: 0.75em 1em;
    background-color: #fff3cd;
    border: 1px solid #ffe69c;
    border-radius: 5px;
}

.iu-warning-dismiss {
    background: none;
    border: none;
    cursor: pointer;
    float: right;
    font-size: 1.1em;
    line-height: 1;
}

.iu-error {
    margin-top: 1em;
    padding: 0.75em 1em;
    background-color: #f8d7da;
    border: 1px solid #f5c2c7;
    border-radius: 5px;
}

.iu-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.85);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.iu-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 2s linear infinite;
    margin-bottom: 8px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>
